import type { SoftphoneTransferProps, TransferStepViews } from './index.types';
import { useSoftphoneTransfer } from 'Modules/softphone/hooks/useSoftphoneTransfer';
import type { TransferTarget } from 'Modules/softphone/types';
import React, { useCallback } from 'react';
import { cn } from '~/lib/utils';
import { TransferConfirmationView } from './TransferConfirmationView';
import { TransferSearchView } from './TransferSearchView';

/**
 * SoftphoneTransfer - Root container for the transfer flow.
 *
 * Orchestrates the multi-step transfer UI by rendering either the
 * search view (contact/number selection) or the confirmation view
 * based on the current transfer step. Routes the transfer confirmation
 * to the appropriate handler based on the selected transfer type
 * (blind, warm, or voicemail).
 */
export const SoftphoneTransfer = ({ testID, variant = 'popup' }: SoftphoneTransferProps) => {
  const componentTestId = testID ? `${testID}-transfer` : 'transfer';

  const {
    transferStep,
    transferTarget,
    transferType,
    handleSelectContact,
    handleSelectNumber,
    handleBlindTransfer,
    handleWarmTransfer,
    handleVoicemailTransfer,
  } = useSoftphoneTransfer();

  const handleConfirm = useCallback(
    (target: TransferTarget, phoneNumber: string) => {
      switch (transferType) {
        case 'warm':
          void handleWarmTransfer(target, phoneNumber);
          break;
        case 'voicemail':
          void handleVoicemailTransfer(target, phoneNumber);
          break;
        case 'blind':
        default:
          void handleBlindTransfer(target, phoneNumber);
          break;
      }
    },
    [transferType, handleBlindTransfer, handleWarmTransfer, handleVoicemailTransfer]
  );

  const transferStepViews: TransferStepViews = {
    search: (
      <TransferSearchView
        onSelectContact={handleSelectContact}
        onSelectNumber={handleSelectNumber}
        variant={variant}
        testID={componentTestId}
      />
    ),
    confirmation: transferTarget ? (
      <TransferConfirmationView
        onConfirm={handleConfirm}
        testID={componentTestId}
      />
    ) : null,
  };

  return (
    <section
      className={cn(
        'flex flex-col w-full overflow-hidden',
        variant === 'popup' ? 'flex-1 min-h-0' : 'h-full'
      )}
      data-testid={componentTestId}
      aria-label="Call transfer"
    >
      {transferStepViews[transferStep]}
    </section>
  );
};

SoftphoneTransfer.displayName = 'SoftphoneTransfer';
